import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, List, BellRing, Settings, CalendarDays, LineChart, ClipboardList, FileSpreadsheet, ChevronLeft, ChevronRight, MapPin, RefreshCcw, LogOut, Shield, ShieldCheck, User, Boxes } from 'lucide-react';
import { useAuth } from '../AuthContext';

const ROLE_ICONS = {
    admin: ShieldCheck,
    manager: Shield,
    picker: User,
};

const Sidebar = ({ isCollapsed, toggleSidebar, user }) => {
    const location = useLocation();
    const { logout } = useAuth();

    const isActive = (path) => location.pathname === path ? 'active' : '';

    const isPicker = user?.role === 'picker';
    const RoleIcon = ROLE_ICONS[user?.role] || User;

    return (
        <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
            <button className="sidebar-toggle-btn" onClick={toggleSidebar} data-tooltip={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
                {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
            <div className="sidebar-header">
                <div className="sidebar-logo-icon">
                    {/* A simple cube icon representation */}
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                        <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    </svg>
                </div>
                <div className="sidebar-logo-text">Shipcube Invecta</div>
            </div>

            <nav className="sidebar-nav-main">
                <div className="nav-item-wrapper" data-tooltip="Dashboard">
                    <Link to="/" className={`nav-item ${isActive('/')}`}>
                        <LayoutDashboard size={20} />
                        <span>Dashboard</span>
                    </Link>
                </div>
                <div className="nav-item-wrapper" data-tooltip="Inventory Records">
                    <Link to="/inventory" className={`nav-item ${isActive('/inventory')}`}>
                        <List size={20} />
                        <span>Inventory Records</span>
                    </Link>
                </div>
                <div className="nav-item-wrapper" data-tooltip="Kit Products">
                    <Link to="/kit-products" className={`nav-item ${isActive('/kit-products')}`}>
                        <Boxes size={20} />
                        <span>Kit Products</span>
                    </Link>
                </div>
                <div className="nav-item-wrapper" data-tooltip="Warehouse Bins">
                    <Link to="/locations" className={`nav-item ${isActive('/locations')}`}>
                        <MapPin size={20} />
                        <span>Warehouse Bins</span>
                    </Link>
                </div>
                <div className="nav-item-wrapper" data-tooltip="Cycle Count">
                    <Link to="/cycle-count" className={`nav-item ${isActive('/cycle-count')}`}>
                        <RefreshCcw size={20} />
                        <span>Cycle Count</span>
                    </Link>
                </div>
 
                {/* Admin/Manager only pages */}
                {!isPicker && (
                    <>
                        <div className="nav-item-wrapper" data-tooltip="Fulfillment Forecast">
                            <Link to="/forecast" className={`nav-item ${isActive('/forecast')}`}>
                                <LineChart size={20} />
                                <span>Fulfillment Forecast</span>
                            </Link>
                        </div>
                        <div className="nav-item-wrapper" data-tooltip="Inbound PO Receipts">
                            <Link to="/po-receipts" className={`nav-item ${isActive('/po-receipts')}`}>
                                <ClipboardList size={20} />
                                <span>Inbound PO Receipts</span>
                            </Link>
                        </div>
                        <div className="nav-item-wrapper" data-tooltip="Daily Snapshots">
                            <Link to="/snapshots" className={`nav-item ${isActive('/snapshots')}`}>
                                <CalendarDays size={20} />
                                <span>Daily Snapshots</span>
                            </Link>
                        </div>
                        <div className="nav-item-wrapper" data-tooltip="Raw Exports">
                            <Link to="/exports" className={`nav-item ${isActive('/exports')}`}>
                                <FileSpreadsheet size={20} />
                                <span>Raw Exports</span>
                            </Link>
                        </div>
                        <div className="nav-item-wrapper" data-tooltip="Alerts">
                            <Link to="/alerts" className={`nav-item ${isActive('/alerts')}`}>
                                <BellRing size={20} />
                                <span>Alerts</span>
                            </Link>
                        </div>
                    </>
                )}
            </nav>
 
            <div className="sidebar-nav-footer">
                {/* Settings - Admin/Manager only */}
                {!isPicker && (
                    <div className="nav-item-wrapper" data-tooltip="Settings">
                        <Link to="/settings" className={`nav-item ${isActive('/settings')}`}>
                            <Settings size={20} />
                            <span>Settings</span>
                        </Link>
                    </div>
                )}

                {/* User info */}
                <div className="sidebar-user-info">
                    <div className="sidebar-user-avatar">
                        <RoleIcon size={16} />
                    </div>
                    <div className="sidebar-user-details">
                        <span className="sidebar-user-name">{user?.username}</span>
                        <span className="sidebar-user-role">{user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}</span>
                    </div>
                </div>

                {/* Logout */}
                <div className="nav-item-wrapper" data-tooltip="Logout">
                    <button className="nav-item sidebar-logout-btn" onClick={logout}>
                        <LogOut size={20} />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
